import AnimationClip from '../AnimationClip';
import { Tween, Easing } from '@tweenjs/tween.js';
import GameObject from '../../../GameObject/GameObject';
import ElementComponent from '../../../Component/UI/Element';
import Renderable from '../../../Component/Renderable';
import { AwaitIterators, StartAnimation } from '../AnimationHelper';
import ColorFilter from '../../../Component/ColorFilter';
import UITextComponent from '../../../Component/UI/Text';

interface IFillBarStreakData {
    progress: number;
    cards: number;
}

export class FillBarStreak extends AnimationClip {
    public *execute(
        gameObject: GameObject,
        meta: IFillBarStreakData
    ): IterableIterator<number> {
        const fill: GameObject = gameObject.find('fill');
        const gift: GameObject = gameObject.find('hud_streak_giftbox');
        const text: GameObject = gameObject.find('streak_text');
        const box: GameObject = gift.find('box');

        const fillElem: ElementComponent = fill.getComponent(ElementComponent);
        const giftElem: ElementComponent = gift.getComponent(ElementComponent);
        const textElem: ElementComponent = text.getComponent(ElementComponent);
        const boxElem: ElementComponent = box.getComponent(ElementComponent);

        textElem.scale.set(1, 1);
        giftElem.rotation = 0;
        boxElem.scale.set(0.55, 0.55);

        const scaleTween: Tween = this.getTween(fillElem.scale);
        scaleTween.to({ x: meta.progress }, 200).easing(Easing.Linear.None);

        const glow: GameObject = gameObject.find('glow');
        const glowRenderer: Renderable = glow.getComponent(Renderable);

        glowRenderer.alpha = 0;

        if (meta.progress === 1) {
            const alphaTween: Tween = this.getTween(glowRenderer);
            alphaTween.to({ alpha: 1 }, 200).easing(Easing.Quadratic.Out);

            StartAnimation(gift, 'GiftBoxOpen');

            yield* AwaitIterators([
                this.startTween(scaleTween),
                this.startTween(alphaTween),
            ]);

            fillElem.scale.x = 0;

            alphaTween.to({ alpha: 0 }, 500).easing(Easing.Quadratic.In);

            yield* this.startTween(alphaTween);
            text.getComponent(UITextComponent).text = meta.cards.toString();
        } else if (meta.progress > 0) {
            text.getComponent(UITextComponent).text = meta.cards.toString();
            yield* AwaitIterators([
                this.giftWiggle(gift, box, text),
                this.startTween(scaleTween),
            ]);
        } else {
            text.getComponent(UITextComponent).text = meta.cards.toString();
            yield* this.startTween(scaleTween);
        }
    }

    private *giftWiggle(gift: GameObject, box: GameObject, text: GameObject) {
        const giftElem: ElementComponent = gift.getComponent(ElementComponent);
        const boxElem: ElementComponent = box.getComponent(ElementComponent);
        const textElem: ElementComponent = text.getComponent(ElementComponent);

        let colorFilter: ColorFilter = gift.getComponent(ColorFilter);
        if (!colorFilter) {
            colorFilter = gift.addComponent<ColorFilter>(ColorFilter);
        }

        giftElem.rotation = 0;
        boxElem.scale.set(0.55, 0.55);
        colorFilter.brightness = 1;

        const scaleTween: Tween = this.getTween(boxElem.scale);
        scaleTween
            .to({ x: 0.65, y: 0.65 }, 200)
            .yoyo(true)
            .repeat(1)
            .start(this.elapsedTime);

        const colorTween: Tween = this.getTween(colorFilter);
        colorTween
            .to({ brightness: 1.5 }, 200)
            .yoyo(true)
            .repeat(1)
            .start(this.elapsedTime)
            .onComplete(() => {
                if (gift && !gift.destroyed) {
                    gift.removeComponent(ColorFilter);
                }
            });

        const txtScaleTween: Tween = this.getTween(textElem.scale);
        txtScaleTween
            .to({ x: 1.5, y: 1.5 }, 200)
            .yoyo(true)
            .repeat(1)
            .start(this.elapsedTime);

        const rotationTween: Tween = this.getTween(giftElem);

        const amp = (15 / 180) * Math.PI;

        yield* this.rotateTo(rotationTween, -amp, 100);
        yield* this.rotateTo(rotationTween, amp, 100);
        yield* this.rotateTo(rotationTween, -amp, 100);
        yield* this.rotateTo(rotationTween, amp, 100);
        yield* this.rotateTo(rotationTween, 0, 100);
    }

    private *rotateTo(
        tween: Tween,
        rotation: number,
        delay: number
    ): IterableIterator<number> {
        tween.to({ rotation: rotation }, delay);
        yield* this.startTween(tween);
    }
}
